'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { User, Key, AlertTriangle } from 'lucide-react';
import { login } from './actions';
import { ThemeSwitch } from '@/components/ThemeSwitch';

export default function LoginPage() {
  const router = useRouter();
  const [error, setError] = useState('');
  
  // Read the environment variable to check if localhost mode is active
  const isLocalhostModeActive = process.env.NEXT_PUBLIC_LOCALHOST_MODE === 'true';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const result = await login(formData);

    if (result.success) {
      router.push('/admin');
    } else {
      // Překlad chybových zpráv z akce
      if (result.error === 'Invalid username or password.') {
        setError('Neplatné uživatelské jméno nebo heslo.');
      } else if (result.error === 'Username and password are required.') {
        setError('Uživatelské jméno a heslo jsou povinné.');
      }
      else {
        setError(result.error || 'Došlo k neznámé chybě.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute top-4 right-4">
            <ThemeSwitch />
        </div>
      <div className="w-full max-w-md">
        <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-8">
          
          <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-gray-100 mb-6">
            Přihlášení pro správce
          </h2>

          {isLocalhostModeActive && (
            <div className="bg-yellow-100 dark:bg-yellow-900/40 border-l-4 border-yellow-500 dark:border-yellow-400 text-yellow-800 dark:text-yellow-200 p-4 mb-6 rounded-md shadow-sm" role="alert">
                <div className="flex">
                    <div className="py-1"><AlertTriangle className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-3" /></div>
                    <div>
                        <p className="font-bold">Aktivní režim Localhost</p>
                        <p className="text-sm">Kliknutím na "Přihlásit se" obejdete zadávání hesla.</p>
                    </div>
                </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <User
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                name="username"
                type="text"
                placeholder="Uživatelské jméno"
                required
                disabled={isLocalhostModeActive}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 disabled:bg-gray-200 dark:disabled:bg-gray-700/50"
              />
            </div>
            <div className="relative">
              <Key
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                name="password"
                type="password"
                placeholder="Heslo"
                required
                disabled={isLocalhostModeActive}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 disabled:bg-gray-200 dark:disabled:bg-gray-700/50"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
            >
              Přihlásit se
            </button>
          </form>
        </div>
        <p className="text-center mt-4">
          <Link href="/" className="text-blue-600 dark:text-blue-400 hover:underline">
            Zpět na hlavní stránku
          </Link>
        </p>
      </div>
    </div>
  );
}
